import { compose } from 'redux';
import { withProfile } from 'modules/v2/containers';
import { withAuth } from 'modules/auth/containers';
import DashboardTemplate from 'modules/v2/templates/DashboardTemplate';

import { useEffect, useState } from 'react';

import { getInstance as getDSInstance } from 'modules/api/digitalServices/instance';

import { Loader, ReportIcon } from 'modules/v2/common/components';
import { Button } from 'modules/v2/common/AtomicDesign/atoms';

import { Step1, isStep1Valid } from 'modules/v2/pages/TemplatesBuilder/shared-components/SetupModal/Step1';
import { Step2, isStep2Valid } from 'modules/v2/pages/TemplatesBuilder/shared-components/SetupModal/Step2';
import { Step3, isStep3Valid } from 'modules/v2/pages/TemplatesBuilder/shared-components/SetupModal/Step3';
import { Step4, isStep4Valid } from 'modules/v2/pages/TemplatesBuilder/shared-components/SetupModal/Step4';

import { DesignHuddleProvider, useDesignHuddle } from 'modules/v2/pages/TemplatesBuilder/DesignHuddleContext';
import { uploadBrokerLogo, uploadHeadshot } from 'modules/v2/pages/TemplatesBuilder/utils/api';
import {
  getStepTitle,
  getStepDescription,
  getInitialFormData,
  initializeFormData,
  prepareTemplateDynamicData
} from 'modules/v2/pages/TemplatesBuilder/utils/setupModalUtils';
import { isEmpty } from 'lodash';

import { notification } from 'modules/v2/common/utils';

const SetupPage = () => {
  const { bookList, userProfile, templateDynamicInfo, isLoadingDynamicInfo, isLoadingBooks } = useDesignHuddle();

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData());

  const handleFirstStepUpdate = async () => {
    if (typeof formData?.headshot !== 'string') {
      const finalS3Url = await uploadHeadshot(formData?.headshot);
      return finalS3Url;
    }
    return formData?.headshot;
  };

  const handleSecondStepUpdate = async () => {
    if (typeof formData?.brokerLogo !== 'string') {
      const finalS3Url = await uploadBrokerLogo(formData?.brokerLogo);
      return finalS3Url;
    }
    return formData?.brokerLogo;
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);

      let updatedFormData = { ...formData };

      if (currentStep === 1 && formData?.headshot) {
        const headshotUrl = await handleFirstStepUpdate();
        updatedFormData = { ...updatedFormData, headshot: headshotUrl };
        setFormData(updatedFormData);
      } else if (currentStep === 2 && formData?.brokerLogo) {
        const brokerLogoUrl = await handleSecondStepUpdate();
        updatedFormData = { ...updatedFormData, brokerLogo: brokerLogoUrl };
        setFormData(updatedFormData);
      }

      const data = prepareTemplateDynamicData(updatedFormData);
      await getDSInstance().post('/template-builder', data);

      if (currentStep < 4)
        setCurrentStep(prev => prev + 1);
    } catch (error) {
      console.error('Error saving changes:', error);

      notification.error({
        title: 'Error Saving Changes',
        description: 'Failed to save default information, if the problem persists, contact the support',
      });
    } finally {
      notification.success({
        title: 'Changes saved',
        description: 'Your default information has been updated successfully',
      });

      setIsLoading(false);
    }
  };

  const isValid = () => {
    if (currentStep === 1)
      return isStep1Valid(formData);

    if (currentStep === 2)
      return isStep2Valid(formData);

    if (currentStep === 3)
      return isStep3Valid(formData);

    if (currentStep === 4)
      return isStep4Valid(formData);

    return true;
  };

  const renderCurrentStep = () => {
    if (!userProfile || isLoadingDynamicInfo || isLoadingBooks) {
      return <Loader height="30vh" icon={<ReportIcon />} />;
    }

    switch (currentStep) {
      case 1:
        return (
          <Step1
            formData={formData}
            onFormDataChange={setFormData}
          />
        );
      case 2:
        return (
          <Step2
            formData={formData}
            onFormDataChange={setFormData}
          />
        );
      case 3:
        return (
          <Step3
            formData={formData}
            bookData={bookList}
            onFormDataChange={setFormData}
          />
        );
      case 4:
        return (
          <Step4
            formData={formData}
            onFormDataChange={setFormData}
          />
        );
      default:
        return null;
    }
  };

  const isWithoutBooksPublished = () => Boolean(!bookList || isEmpty(bookList));

  const isSaveDisabled = () => Boolean(
    !isValid() ||
    isLoading ||
    !userProfile ||
    isLoadingDynamicInfo ||
    isLoadingBooks ||
    currentStep === 3 && isWithoutBooksPublished()
  );

  useEffect(() => {
    if (userProfile && !isLoadingDynamicInfo) {
      const initializedData = initializeFormData(userProfile, templateDynamicInfo);
      setFormData(initializedData);
    }
  }, [userProfile, templateDynamicInfo, isLoadingDynamicInfo]);

  return (
    <>
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[1, 2, 3, 4].map((step) => (
            <button
              key={step}
              onClick={() => setCurrentStep(step)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                currentStep === step
                  ? 'border-cyan-500 text-neutral-800'
                  : 'border-transparent text-neutral-600 hover:text-neutral-700 hover:border-gray-300'
              }`}
            >
              {getStepTitle(step)}
            </button>
          ))}
        </nav>
      </div>

      <div className='bg-white rounded-lg shadow-md font-figtree overflow-y-auto'>
        <div className="px-[24px] py-[22px]">
          <h2 className="text-xl font-bold">{getStepTitle(currentStep)}</h2>
          <span className="text-sm text-neutral-400 leading-5 font-medium">{getStepDescription(currentStep)}</span>
        </div>

        <hr />

        {/* <div className="max-h-[90vh] overflow-y-auto"> */}
        <div className="px-[24px] py-[22px] min-h-[15vh]">
          {renderCurrentStep()}
        </div>

        <hr />

        <div className="flex items-center px-[24px] py-[22px]">
          <Button
            type="primary"
            onClick={handleSave}
            disabled={isSaveDisabled()}
          >
            {isLoading ? 'Loading...' : 'Save Changes'}
          </Button>

          {currentStep === 3 && isWithoutBooksPublished() && <Button
            type="primary"
            className="ml-4"
            onClick={() => window.location.replace('/my-books/book-catalog')}
          >
            View Book Catalog
          </Button>}
        </div>
      </div>
    </>
  );
};

const SetupWrapper = (props) => {
  return (
    <DesignHuddleProvider>
      <DashboardTemplate
        title="Template Defaults" {...props}
        titleClass="text-2xl font-bold flex-1 mb-2"
        centralized
        subHeader={(
          <div className="mb-8 leading-5 font-medium">
            Use template defaults to automatically edit your personal info, contact details, and book info across all templates.
          </div>
        )}
      >
        <SetupPage />
      </DashboardTemplate>
    </DesignHuddleProvider>
  );
};

export default compose(withProfile, withAuth)(SetupWrapper);
